import { useEffect, useRef, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { loginSchema, type TLoginFormData } from './schema';

export const Route = createFileRoute('/login/')({
  component: Login,
});

function Login() {
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const loadingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<TLoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = async (data: TLoginFormData) => {
    // Exibe loading apenas se a solicitação demorar > 300ms (evita flicker)
    if (loadingTimerRef.current) clearTimeout(loadingTimerRef.current);
    loadingTimerRef.current = setTimeout(() => setLoading(true), 300);

    try {
      const result = await login(data.email, data.password);
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }
      if (result?.ok) {
        // O RouterProvider só propaga o auth novo pro contexto do router no
        // próximo render; navegar na hora faz o guard de _authenticated ainda
        // ver isAuthenticated=false e devolver pro login. Pequeno delay evita a corrida.
        setTimeout(() => navigate({ to: '/dashboard' }), 150);
        return;
      }
      const msg =
        result?.message || (result?.status === 401 ? 'E-mail ou senha incorretos.' : 'Não foi possível realizar o login.');
      toast.error(msg);
      setLoading(false);
    } catch {
      toast.error('Erro ao conectar com o servidor');
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }
      setLoading(false);
    }
  };

  // Limpeza do timer em caso de desmontagem do componente
  useEffect(() => {
    return () => {
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }
    };
  }, []);

  return (
    <div className="flex min-h-screen w-full max-w-[100vw] items-center justify-center bg-gradient-to-br from-surface to-surface-container p-4">
      <div className="w-full max-w-[400px] rounded-2xl border border-outline-variant bg-surface-container-lowest shadow-xl">
        <div className="border-b border-outline-variant p-6 text-center">
          <h1 className="m-0 text-2xl font-semibold text-on-surface">Monitoramento e Estatísticas</h1>
          <p className="mt-2 text-sm text-on-surface-variant">Faça login para acessar o sistema</p>
        </div>

        <div className="p-6">
          <form
            onSubmit={(e) => void handleSubmit(onSubmit)(e)}
            noValidate
          >
            <FieldGroup>
              <Field data-invalid={!!errors.email}>
                <FieldLabel htmlFor="email">E-mail</FieldLabel>
                <Input
                  type="email"
                  id="email"
                  placeholder="Digite seu e-mail"
                  disabled={loading}
                  autoComplete="email"
                  spellCheck={false}
                  aria-invalid={!!errors.email}
                  {...register('email')}
                />
                <FieldError errors={errors.email ? [errors.email] : undefined} />
              </Field>

              <Field data-invalid={!!errors.password}>
                <FieldLabel htmlFor="password">Senha</FieldLabel>
                <Input
                  type="password"
                  id="password"
                  placeholder="Digite sua senha"
                  disabled={loading}
                  autoComplete="current-password"
                  aria-invalid={!!errors.password}
                  {...register('password')}
                />
                <FieldError errors={errors.password ? [errors.password] : undefined} />
              </Field>

              <Button
                type="submit"
                className="mt-2 w-full"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Spinner data-icon="inline-start" />
                    Entrando...
                  </>
                ) : (
                  'Entrar'
                )}
              </Button>
            </FieldGroup>
          </form>
        </div>

        <div className="border-t border-outline-variant p-6 text-center">
          <p className="m-0 text-xs text-on-surface-variant">Sistema de Monitoramento e Estatísticas</p>
        </div>
      </div>
    </div>
  );
}
